import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MapPin, Phone, Instagram, Clock, Star, ExternalLink, AlertCircle, Send, Bookmark, Sparkles, MessageSquare, MessageCircle, CalendarCheck } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { toast } from "sonner";
import BookingDialog from "../components/BookingDialog";
import InstagramEmbed from "../components/InstagramEmbed";

const DAY_NAMES = {
  monday: "Senin", tuesday: "Selasa", wednesday: "Rabu",
  thursday: "Kamis", friday: "Jumat", saturday: "Sabtu", sunday: "Minggu"
};

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const isOpenNow = (hours, override) => {
  if (override === true) return { open: true, label: "Buka" };
  if (override === false) return { open: false, label: "Tutup (Hari ini)" };
  if (!hours) return { open: false, label: "—" };
  const now = new Date();
  const dayKey = DAY_KEYS[(now.getDay() + 6) % 7];
  const slot = hours[dayKey];
  if (!slot) return { open: false, label: "Tutup" };
  const [start, end] = slot.split("-");
  if (!start || !end) return { open: false, label: "Tutup" };
  const cur = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const startM = sh * 60 + sm;
  const endM = eh * 60 + em;
  return cur >= startM && cur <= endM ? { open: true, label: `Buka • Tutup ${end}` } : { open: false, label: `Tutup • Buka ${start}` };
};

const RestaurantDetail = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [menu, setMenu] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [userReviews, setUserReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewForm, setReviewForm] = useState({ rating: 5, content: "" });
  const [bookmarked, setBookmarked] = useState(false);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [reviewSearch, setReviewSearch] = useState("");
  const [influencerSearch, setInfluencerSearch] = useState("");

  const isOwner = user && r && r.owner_id === user.user_id;
  const isFeatured = r?.featured_until && new Date(r.featured_until) > new Date();

  const load = async () => {
    setLoading(true);
    try {
      const r1 = await api.get(`/restaurants/${slug}`);
      setR(r1.data);
      const reviewQs = new URLSearchParams({ restaurant_id: slug });
      if (reviewSearch) reviewQs.set("q", reviewSearch);
      if (influencerSearch) reviewQs.set("influencer_name", influencerSearch);
      const [m, rv, ur] = await Promise.all([
        api.get(`/restaurants/${slug}/menu`),
        api.get(`/reviews?${reviewQs.toString()}`),
        api.get(`/user-reviews?restaurant_id=${slug}`),
      ]);
      setMenu(m.data);
      setReviews(rv.data);
      setUserReviews(ur.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [slug]);

  // Re-fetch reviews when search changes
  useEffect(() => {
    if (!r) return;
    const t = setTimeout(() => {
      const reviewQs = new URLSearchParams({ restaurant_id: slug });
      if (reviewSearch) reviewQs.set("q", reviewSearch);
      if (influencerSearch) reviewQs.set("influencer_name", influencerSearch);
      api.get(`/reviews?${reviewQs.toString()}`).then(res => setReviews(res.data));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewSearch, influencerSearch]);

  useEffect(() => {
    if (user && r) {
      api.get(`/bookmarks/check/${r.id}`).then(res => setBookmarked(res.data.bookmarked)).catch(() => {});
    }
  }, [user, r]);

  const toggleBookmark = async () => {
    if (!user) { toast.error("Login dulu untuk simpan ke favorit"); return; }
    try {
      const res = await api.post(`/bookmarks/toggle/${r.id}`);
      setBookmarked(res.data.bookmarked);
      toast.success(res.data.bookmarked ? "Disimpan ke favorit" : "Dihapus dari favorit");
    } catch {
      toast.error("Gagal menyimpan");
    }
  };

  const submitReply = async (reviewId) => {
    const text = (replyDrafts[reviewId] || "").trim();
    if (!text) return;
    try {
      await api.post(`/user-reviews/${reviewId}/reply`, { reply: text });
      toast.success("Balasan terkirim");
      setReplyDrafts({ ...replyDrafts, [reviewId]: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim balasan");
    }
  };

  const submitUserReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Login dulu untuk memberi rating"); return; }
    try {
      await api.post("/user-reviews", {
        restaurant_id: r.id,
        rating: parseFloat(reviewForm.rating),
        content: reviewForm.content,
      });
      toast.success("Review berhasil dikirim!");
      setReviewForm({ rating: 5, content: "" });
      load();
    } catch (e) {
      toast.error("Gagal mengirim review");
    }
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground">Memuat...</div>;
  if (!r) return <div className="text-center py-20">Restoran tidak ditemukan</div>;

  const status = isOpenNow(r.opening_hours, r.is_open_override);
  const mapsEmbed = r.lat && r.lng
    ? `https://www.google.com/maps?q=${r.lat},${r.lng}&output=embed`
    : `https://www.google.com/maps?q=${encodeURIComponent(r.address)}&output=embed`;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8" data-testid="restaurant-detail">
      {/* Cover */}
      <div className="relative h-72 lg:h-96 rounded-3xl overflow-hidden mb-8">
        <img src={r.cover_image} alt={r.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <button
          onClick={toggleBookmark}
          data-testid="bookmark-btn"
          className={`absolute top-5 right-5 w-12 h-12 rounded-full backdrop-blur flex items-center justify-center transition-all ${bookmarked ? "bg-primary text-primary-foreground" : "bg-white/20 text-white hover:bg-white/30"}`}
        >
          <Bookmark className={`w-5 h-5 ${bookmarked ? "fill-current" : ""}`} />
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {isFeatured && (
              <Badge className="bg-gradient-to-r from-amber-500 to-primary text-white border-0" data-testid="featured-badge">
                <Sparkles className="w-3 h-3 mr-1" /> Featured {r.featured_tier?.toUpperCase()}
              </Badge>
            )}
            <Badge className="bg-primary/90 backdrop-blur">{r.category?.replace("-", " ")}</Badge>
            {r.price_range && <Badge variant="secondary" className="bg-white/20 backdrop-blur text-white border-white/30">{r.price_range}</Badge>}
            <Badge className={status.open ? "bg-green-600/90" : "bg-red-600/90"} data-testid="open-status">
              <Clock className="w-3 h-3 mr-1" /> {status.label}
            </Badge>
          </div>
          <h1 className="font-heading text-4xl lg:text-6xl font-black tracking-tight">{r.name}</h1>
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {r.address}</span>
            {r.avg_rating && (
              <span className="flex items-center gap-1.5">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" /> {r.avg_rating.toFixed(1)} ({r.review_count} reviews)
              </span>
            )}
          </div>
        </div>
      </div>

      {r.holiday_announcement && (
        <Alert className="mb-8 border-primary bg-primary/10" data-testid="holiday-announcement">
          <AlertCircle className="h-4 w-4 text-primary" />
          <AlertTitle className="text-primary font-bold">Pengumuman dari Owner</AlertTitle>
          <AlertDescription className="text-foreground/80">{r.holiday_announcement}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT - sticky info */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-6">
            <div className="warm-card p-6">
              <h3 className="font-heading font-bold text-lg mb-4">Lokasi & Kontak</h3>
              <div className="aspect-square w-full rounded-xl overflow-hidden border border-border mb-4">
                <iframe src={mapsEmbed} className="w-full h-full" allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="map" />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" /> <span>{r.address}</span></div>
                {r.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-primary" /> {r.phone}</div>}
                {r.instagram && (
                  <a href={`https://instagram.com/${r.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline">
                    <Instagram className="w-4 h-4" /> {r.instagram}
                  </a>
                )}
                {r.google_maps_url && (
                  <a href={r.google_maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline">
                    <ExternalLink className="w-4 h-4" /> Buka di Google Maps
                  </a>
                )}
              </div>

              {/* Direct Actions: Booking + WhatsApp & Reservation */}
              {(r.booking_enabled || r.whatsapp_number || r.reservation_enabled) && (
                <div className="mt-4 pt-4 border-t border-border space-y-2" data-testid="direct-actions">
                  {r.booking_enabled && <BookingDialog restaurant={r} />}
                  {r.whatsapp_number && (
                    <a
                      href={`https://wa.me/${r.whatsapp_number.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Halo, saya dari Kulintara ingin tanya tentang ${r.name}`)}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="whatsapp-btn"
                    >
                      <Button variant="outline" className="w-full rounded-full bg-green-50 hover:bg-green-100 border-green-300 text-green-700">
                        <MessageCircle className="w-4 h-4 mr-2" /> Chat via WhatsApp
                      </Button>
                    </a>
                  )}
                  {r.reservation_enabled && !r.booking_enabled && (
                    <a
                      href={r.whatsapp_number ? `https://wa.me/${r.whatsapp_number.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Halo, saya ingin reservasi meja di ${r.name}.`)}` : "#"}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="reserve-btn"
                    >
                      <Button variant="outline" className="w-full rounded-full">
                        <CalendarCheck className="w-4 h-4 mr-2" /> Request Reservasi
                      </Button>
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="warm-card p-6" data-testid="opening-hours">
              <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" /> Jam Buka
              </h3>
              <ul className="space-y-1.5 text-sm">
                {DAY_KEYS.map(d => (
                  <li key={d} className="flex justify-between">
                    <span className="text-muted-foreground">{DAY_NAMES[d]}</span>
                    <span className="font-medium">{r.opening_hours?.[d] || "Tutup"}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>

        {/* RIGHT - menu, reviews */}
        <main className="lg:col-span-8 space-y-12">
          {r.description && (
            <section>
              <h2 className="font-heading text-2xl font-black mb-4">Tentang</h2>
              <p className="text-foreground/80 leading-relaxed">{r.description}</p>
            </section>
          )}

          {/* MENU */}
          <section data-testid="menu-section">
            <h2 className="font-heading text-2xl font-black mb-6">Menu</h2>
            {menu.length === 0 ? (
              <p className="text-muted-foreground">Belum ada menu.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {menu.map(m => (
                  <div key={m.id} className="warm-card flex gap-4 p-3" data-testid={`menu-item-${m.id}`}>
                    {m.photo && <img src={m.photo} alt={m.name} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold leading-tight">{m.name}</h4>
                      {m.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{m.description}</p>}
                      <p className="font-heading font-black text-primary mt-2">Rp {m.price.toLocaleString("id-ID")}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* INFLUENCER REVIEWS */}
          <section data-testid="influencer-reviews-section">
            <h2 className="font-heading text-2xl font-black mb-4">Review Influencer</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="review-search-bar">
              <Input
                placeholder="Cari di review..."
                value={reviewSearch}
                onChange={(e) => setReviewSearch(e.target.value)}
                data-testid="review-search-q"
              />
              <Input
                placeholder="Filter by nama influencer..."
                value={influencerSearch}
                onChange={(e) => setInfluencerSearch(e.target.value)}
                data-testid="review-search-influencer"
              />
            </div>
            {reviews.length === 0 ? (
              <p className="text-muted-foreground">Belum ada review.</p>
            ) : (
              <div className="space-y-4">
                {reviews.map(rv => (
                  <div key={rv.id} className="warm-card p-6" data-testid={`influencer-review-${rv.id}`}>
                    <div className="flex items-start gap-4">
                      <Link to={`/influencers/${rv.influencer?.user_id}`}>
                        <img src={rv.influencer?.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                      </Link>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Link to={`/influencers/${rv.influencer?.user_id}`} className="font-bold hover:text-primary">{rv.influencer?.name}</Link>
                          <span className="text-xs text-muted-foreground">{rv.influencer?.instagram_handle}</span>
                        </div>
                        <h3 className="font-heading text-lg font-bold leading-tight">{rv.title}</h3>
                        {rv.content && <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{rv.content}</p>}
                        <div className="grid grid-cols-4 gap-3 mt-4 text-center text-sm border-t border-border pt-4">
                          <div><div className="text-xs text-muted-foreground uppercase tracking-wider">Rasa</div><div className="font-heading font-black text-lg text-primary">{rv.rasa}</div></div>
                          <div><div className="text-xs text-muted-foreground uppercase tracking-wider">Harga</div><div className="font-heading font-black text-lg text-primary">{rv.harga}</div></div>
                          <div><div className="text-xs text-muted-foreground uppercase tracking-wider">Ambience</div><div className="font-heading font-black text-lg text-primary">{rv.ambience}</div></div>
                          <div><div className="text-xs text-muted-foreground uppercase tracking-wider">Score</div><div className="font-heading font-black text-lg text-primary">{rv.overall}</div></div>
                        </div>
                        {rv.instagram_video_url && (
                          <div className="mt-4">
                            <InstagramEmbed url={rv.instagram_video_url} />
                            <a href={rv.instagram_video_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline" data-testid={`ig-video-${rv.id}`}>
                              <Instagram className="w-4 h-4" /> Buka di Instagram <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* USER REVIEWS */}
          <section data-testid="user-reviews-section">
            <h2 className="font-heading text-2xl font-black mb-6">Review Pengunjung</h2>
            {user ? (
              <form onSubmit={submitUserReview} className="warm-card p-6 mb-6 space-y-3" data-testid="user-review-form">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Rating:</span>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      type="button"
                      key={n}
                      onClick={() => setReviewForm({ ...reviewForm, rating: n })}
                      data-testid={`star-${n}`}
                    >
                      <Star className={`w-6 h-6 ${n <= reviewForm.rating ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                    </button>
                  ))}
                </div>
                <Textarea
                  required
                  data-testid="user-review-content"
                  value={reviewForm.content}
                  onChange={(e) => setReviewForm({ ...reviewForm, content: e.target.value })}
                  placeholder="Bagaimana pengalamanmu di sini?"
                />
                <Button type="submit" className="rounded-full" data-testid="submit-user-review">
                  <Send className="w-4 h-4 mr-2" /> Kirim Review
                </Button>
              </form>
            ) : (
              <p className="text-sm text-muted-foreground mb-4">Login untuk meninggalkan review.</p>
            )}
            {userReviews.length === 0 ? (
              <p className="text-muted-foreground">Belum ada review pengunjung.</p>
            ) : (
              <div className="space-y-4">
                {userReviews.map(ur => (
                  <div key={ur.id} className="warm-card p-5" data-testid={`user-review-${ur.id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <img src={ur.user?.picture || `https://ui-avatars.com/api/?name=${ur.user?.name}`} alt="" className="w-8 h-8 rounded-full" />
                      <div>
                        <div className="font-semibold text-sm">{ur.user?.name}</div>
                        <div className="flex">
                          {[1,2,3,4,5].map(n => (
                            <Star key={n} className={`w-3 h-3 ${n <= ur.rating ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground/80">{ur.content}</p>

                    {ur.owner_reply && (
                      <div className="mt-3 ml-6 pl-4 border-l-2 border-primary bg-secondary/50 p-3 rounded-r-lg" data-testid={`owner-reply-${ur.id}`}>
                        <div className="flex items-center gap-2 text-xs text-primary font-bold mb-1">
                          <MessageSquare className="w-3 h-3" /> Balasan dari Owner
                        </div>
                        <p className="text-sm text-foreground/80">{ur.owner_reply}</p>
                      </div>
                    )}

                    {isOwner && !ur.owner_reply && (
                      <div className="mt-3 ml-6" data-testid={`owner-reply-form-${ur.id}`}>
                        <Textarea
                          rows={2}
                          placeholder="Balas review pelanggan..."
                          value={replyDrafts[ur.id] || ""}
                          onChange={(e) => setReplyDrafts({ ...replyDrafts, [ur.id]: e.target.value })}
                          data-testid={`reply-textarea-${ur.id}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => submitReply(ur.id)}
                          className="mt-2 rounded-full"
                          data-testid={`reply-submit-${ur.id}`}
                        >
                          <Send className="w-3 h-3 mr-1" /> Kirim Balasan
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default RestaurantDetail;
